import { Trans } from '@lingui/react';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Dialog, DialogTitle } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import { ResponsiveGridColDef } from 'components/DataGrid';
import { PAGINATION_PAGE_SIZES } from 'config';
import { i18n } from 'i18n';
import {
  AutoCreatedRecipeDto,
  ProjectDto,
  useAutoCreatedRecipesQuery,
  useUndoAutoRecipesMutation
} from 'providers/api';
import { useConfirmation } from 'providers/confirm';
import { usePage } from 'providers/page';
import { useScenario } from 'modules/project/providers/scenario';
import React from 'react';
import { Model } from '../hooks/useModelState';

interface AutoRecipeListMenuProps {
  model: Model;
  recipe: AutoCreatedRecipeDto;
}
const AutoRecipeListMenu = ({ recipe, model }: AutoRecipeListMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const undoAutoRecipes = useUndoAutoRecipesMutation();
  const confirm = useConfirmation();

  const handleDelete = (autoRecipe: AutoCreatedRecipeDto) => {
    confirm({
      variant: 'danger',
      description: i18n._(
        'Are you sure you want to remove all selected automatically created recipes?',
      ),
    }).then(() => {
      undoAutoRecipes.mutate({
        projectId: model.project.entityId as string,
        autoRecipeBatchId: autoRecipe.autoRecipeBatchId,
      });
    });
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-controls={`recipe-actions-menu-${recipe.entityId}`}
        aria-haspopup="true"
        onClick={handleClick}
        size="large"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`recipe-actions-menu-${recipe.entityId}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => handleDelete(recipe)}>
          <ListItemIcon>
            <DeleteForeverIcon color="error" fontSize="small" />
          </ListItemIcon>
          <Typography color="error" variant="inherit">
            <Trans id="Delete">Delete</Trans>
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

interface AutoRecipeListProps {
  model: Model;
  project: ProjectDto;
  onComplete: () => void;
}

const AutoRecipeList = ({
  model,
  project,
  onComplete,
}: AutoRecipeListProps) => {
  const handleCloseModal = () => {
    onComplete();
  };
  const {
    actions: {
      setUndoActionOverride,
      clearUndoActionOverride,
    },
  } = usePage();
  const { activeScenarioId } = useScenario();

  const columns: ResponsiveGridColDef[] = [
    {
      field: 'recipeNames',
      headerName: i18n._('Recipe Name'),
      flex: 1,
      resizable: false,
      sortable: false,
      filterable: false,
      renderCell: ({ value }) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.5' }}>
          {value}
        </div>
      ),
      mobileConfig: {
        width: 200,
        isVisible: true,
      },
    },
    {
      field: 'stopeNames',
      headerName: i18n._('Stopes'),
      flex: 1,
      resizable: false,
      sortable: false,
      filterable: false,
      renderCell: ({ value }) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.5' }}>
          {value}
        </div>
      ),
      mobileConfig: {
        width: 200,
        isVisible: true,
      },
    },
    {
      field: 'creationDate',
      headerName: i18n._('Created On'),
      width: 200,
      resizable: false,
      sortable: false,
      filterable: false,
      mobileConfig: {
        width: 200,
        isVisible: true,
      },
      valueFormatter: (value: string | Date | undefined) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return 'Invalid Date';
        }
        return date.toLocaleString();
      },
    },
    {
      field: '',
      sortable: false,
      filterable: false,
      resizable: false,
      width: 80,
      renderCell: ({ row }) => (
        <AutoRecipeListMenu
          recipe={(row as any) as AutoCreatedRecipeDto}
          model={model}
        />
      ),
    },
  ];

  const {
    data: recipes,
    refetch: refetchAutoRecipes,
  } = useAutoCreatedRecipesQuery({ projectId: project.entityId, scenarioId: activeScenarioId ?? undefined });
  const undoAutoRecipes = useUndoAutoRecipesMutation();
  const undoAutoRecipesRef = React.useRef(undoAutoRecipes);
  undoAutoRecipesRef.current = undoAutoRecipes;

  const latestBatchId = React.useMemo(() => {
    if (!recipes || recipes.length === 0) {
      return null;
    }

    return recipes.reduce((maxBatchId, recipe) => (
      (recipe.autoRecipeBatchId ?? 0) > maxBatchId ? (recipe.autoRecipeBatchId ?? 0) : maxBatchId
    ), recipes[0].autoRecipeBatchId ?? 0);
  }, [recipes]);

  const handleGlobalUndo = React.useCallback(() => {
    if (!latestBatchId) {
      return;
    }

    setUndoActionOverride((current) => (current ? {
      ...current,
      enabled: false,
      loading: true,
    } : current));

    undoAutoRecipesRef.current.mutate(
      {
        projectId: project.entityId,
        autoRecipeBatchId: latestBatchId,
      },
      {
        onSuccess: async () => {
          clearUndoActionOverride();
          await refetchAutoRecipes();
        },
        onError: () => {
          setUndoActionOverride({
            enabled: true,
            loading: false,
            onClick: handleGlobalUndo,
          });
        },
      },
    );
  }, [clearUndoActionOverride, latestBatchId, project.entityId, refetchAutoRecipes, setUndoActionOverride]);

  React.useEffect(() => {
    if (!latestBatchId) {
      setUndoActionOverride({
        enabled: false,
        loading: false,
        onClick: null,
      });
    } else {
      setUndoActionOverride({
        enabled: true,
        loading: false,
        onClick: handleGlobalUndo,
      });
    }

    return () => {
      clearUndoActionOverride();
    };
  }, [clearUndoActionOverride, handleGlobalUndo, latestBatchId, setUndoActionOverride]);

  const rows = recipes?.map((recipe) => ({
    ...recipe,
    id: recipe.entityId,
  })) ?? [];
  const isAutoRecipe = true;

  return (
    <Dialog
      title={i18n._('Automatically Created Recipes')}
      open={isAutoRecipe}
      onClose={handleCloseModal}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle id="simple-dialog-title"><Trans id="Automatically Created Recipes">Automatically Created Recipes</Trans></DialogTitle>
      <Box sx={{
        m: 2,
      }}
      >
        <DataGrid
          // TODO: Check if this needs reinstating
          // disableSelectionOnClick
          disableColumnMenu
          autoHeight
          rows={rows}
          columns={columns}
          pagination
          pageSizeOptions={PAGINATION_PAGE_SIZES}
        />
      </Box>
    </Dialog>
  );
};

export default AutoRecipeList;
